import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { useAuthContext } from 'contexts';
import { Base } from 'layout';
import { useEffect, useState } from 'react';
import { MagentoService } from 'services';

export const Dashboard = () => {
    const { isAdmin, categories } = useAuthContext();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [report, setReport] = useState(null);
    const [dataInicio, setDataInicio] = useState('');
    const [dataFim, setDataFim] = useState('');
    const [weekIndex, setWeekIndex] = useState(0);
    const [monthlyMetric, setMonthlyMetric] = useState('quantidade');
    const [categoriaId, setCategoriaId] = useState('');
    const cardLabelColor = '#6b7280';

    const formatCurrency = (value) => {
        if (value === null || value === undefined) return 'R$ 0,00';
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(Number(value));
    };

    const loadReport = () => {
        setLoading(true);
        setError('');

        MagentoService.getSalesReport({
            dataInicio: dataInicio || undefined,
            dataFim: dataFim || undefined,
            categoriaId: categoriaId || undefined
        }).then((result) => {
            setLoading(false);
            if (result instanceof Error) {
                setError(result.message);
            } else {
                setReport(result);
                setWeekIndex(0);
            }
        });
    };

    useEffect(() => {
        loadReport();
    }, []);

    const salesWeeks = report?.vendas_por_semana || [];
    const currentWeek = salesWeeks[weekIndex];
    const canGoPrev = weekIndex < salesWeeks.length - 1;
    const canGoNext = weekIndex > 0;
    const coupons = report?.cupons_utilizados || [];
    const monthlySales = report?.vendas_por_mes || [];
    const monthlyPackageKey = monthlyMetric === 'valor' ? 'valor_recebido' : 'quantidade_vendida';

    const monthlySalesSorted = [...monthlySales].sort((a, b) => {
        if (a.ano !== b.ano) return a.ano - b.ano;
        return a.mes - b.mes;
    });

    const getPackageColor = (key) => {
        let hash = 0;
        for (let i = 0; i < key.length; i += 1) {
            hash = key.charCodeAt(i) + ((hash << 5) - hash);
        }
        const palette = [
            '#0f5132',
            '#2e7d32',
            '#4caf50',
            '#1b5e20',
            '#a76708',
            '#c77700',
            '#e07a00',
            '#ff8f00',
            '#f28c28',
            '#d97706',
            '#7cb342',
            '#9ccc65'
        ];
        const index = Math.abs(hash) % palette.length;
        return palette[index];
    };

    const packageLegend = monthlySales.reduce((acc, mes) => {
        (mes.pacotes || []).forEach((pacote) => {
            const legendKey = `${pacote.produto_id}-${pacote.sku || ''}`;
            if (!acc[legendKey]) {
                acc[legendKey] = {
                    key: legendKey,
                    nome: pacote.nome,
                    color: getPackageColor(legendKey)
                };
            }
        });
        return acc;
    }, {});

    const packageLegendList = Object.values(packageLegend);
    const monthLabels = monthlySalesSorted.map((mes) => `${String(mes.mes).padStart(2, '0')}/${mes.ano}`);
    const monthlyPackageMap = monthlySalesSorted.map((mes) => {
        const map = new Map();
        (mes.pacotes || []).forEach((pacote) => {
            const key = `${pacote.produto_id}-${pacote.sku || ''}`;
            map.set(key, Number(pacote?.[monthlyPackageKey] || 0));
        });
        return map;
    });
    const monthlySeries = packageLegendList.map((item) => ({
        id: item.key,
        label: item.nome,
        stack: 'total',
        color: item.color,
        data: monthlyPackageMap.map((map) => map.get(item.key) || 0),
        valueFormatter: (value) => (monthlyMetric === 'valor' ? formatCurrency(value) : `${value || 0}`)
    }));

    return (
        <Base
            titulo='Vendas'
            subTitulo='Acompanhe aqui os indicadores e métricas consolidadas do sistema para suporte à tomada de decisão e à gestão administrativa.'>
            <Box
                component={Paper}
                elevation={0}
                variant='outlined'
                marginY={2}
                marginRight={2}
                padding={2}>
                <Grid
                    container
                    spacing={1}>
                    <Grid
                        item
                        xs={12}>
                        <Box
                            display='flex'
                            gap={2}
                            flexWrap='wrap'>
                            <TextField
                                label='Data Inicio'
                                type='date'
                                size='small'
                                value={dataInicio}
                                onChange={(e) => setDataInicio(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                label='Data Fim'
                                type='date'
                                size='small'
                                value={dataFim}
                                onChange={(e) => setDataFim(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                select
                                label='Operação'
                                size='small'
                                value={categoriaId}
                                onChange={(e) => setCategoriaId(e.target.value)}
                                sx={{ minWidth: 200 }}>
                                <MenuItem value=''>Todas</MenuItem>
                                {categories.map((categoria) => (
                                    <MenuItem
                                        key={categoria.id}
                                        value={String(categoria.id)}>
                                        {isAdmin ? categoria.name : categoria.nome}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <Button
                                variant='contained'
                                disableElevation
                                onClick={loadReport}>
                                Buscar
                            </Button>
                        </Box>
                    </Grid>

                    <Grid
                        item
                        xs={12}>
                        {loading && (
                            <Box
                                display='flex'
                                justifyContent='center'
                                padding={2}>
                                <CircularProgress size={44} />
                            </Box>
                        )}
                        {!loading && error && <Typography color='error'>{error}</Typography>}
                    </Grid>

                    {!loading && report && (
                        <Grid
                            item
                            xs={12}>
                            <Box sx={{ overflowX: 'auto' }}>
                                <Box
                                    display='flex'
                                    gap={2}
                                    flexWrap='nowrap'>
                                    <Card
                                        elevation={0}
                                        variant='outlined'
                                        sx={{ minWidth: 170 }}>
                                        <CardContent sx={{ padding: 1, '&:last-child': { paddingBottom: 1 } }}>
                                            <Typography
                                                variant='body2'
                                                sx={{ color: cardLabelColor }}>
                                                Receita Total
                                            </Typography>
                                            <Typography
                                                variant='h6'
                                                sx={{ color: '#2e7d32', fontWeight: 600 }}>
                                                {formatCurrency(report.total_receita_vendas)}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                    <Card
                                        elevation={0}
                                        variant='outlined'
                                        sx={{ minWidth: 150 }}>
                                        <CardContent sx={{ padding: 1, '&:last-child': { paddingBottom: 1 } }}>
                                            <Typography
                                                variant='body2'
                                                sx={{ color: cardLabelColor }}>
                                                Vendas
                                            </Typography>
                                            <Typography
                                                variant='h6'
                                                sx={{ color: '#2e7d32', fontWeight: 600 }}>
                                                {report.total_vendas_pagamento_completo || 0}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                    <Card
                                        elevation={0}
                                        variant='outlined'
                                        sx={{ minWidth: 160 }}>
                                        <CardContent sx={{ padding: 1, '&:last-child': { paddingBottom: 1 } }}>
                                            <Typography
                                                variant='body2'
                                                sx={{ color: cardLabelColor }}>
                                                Pacotes Vendidos
                                            </Typography>
                                            <Typography
                                                variant='h6'
                                                sx={{ color: '#2e7d32', fontWeight: 600 }}>
                                                {report.total_pacotes_vendidos || 0}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                    <Card
                                        elevation={0}
                                        variant='outlined'
                                        sx={{ minWidth: 170 }}>
                                        <CardContent sx={{ padding: 1, '&:last-child': { paddingBottom: 1 } }}>
                                            <Typography
                                                variant='body2'
                                                sx={{ color: cardLabelColor }}>
                                                Viagens Realizadas
                                            </Typography>
                                            <Typography
                                                variant='h6'
                                                sx={{ color: '#2e7d32', fontWeight: 600 }}>
                                                {report.total_vendas_viagens_realizadas || 0}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                    <Card
                                        elevation={0}
                                        variant='outlined'
                                        sx={{ minWidth: 150 }}>
                                        <CardContent sx={{ padding: 1, '&:last-child': { paddingBottom: 1 } }}>
                                            <Typography
                                                variant='body2'
                                                sx={{ color: cardLabelColor }}>
                                                Descontos
                                            </Typography>
                                            <Typography
                                                variant='h6'
                                                sx={{ color: '#dc2626', fontWeight: 600 }}>
                                                {formatCurrency(report.total_descontos)}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                    <Card
                                        elevation={0}
                                        variant='outlined'
                                        sx={{ minWidth: 170 }}>
                                        <CardContent sx={{ padding: 1, '&:last-child': { paddingBottom: 1 } }}>
                                            <Typography
                                                variant='body2'
                                                sx={{ color: cardLabelColor }}>
                                                Valor Recebido
                                            </Typography>
                                            <Typography
                                                variant='h6'
                                                sx={{ color: '#2e7d32', fontWeight: 600 }}>
                                                {formatCurrency(report.total_valor_vendas_pagamento_completo)}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Box>
                            </Box>
                        </Grid>
                    )}
                </Grid>

                {!loading && report && (
                    <Box
                        component={Paper}
                        elevation={0}
                        variant='outlined'
                        marginTop={4}
                        marginRight={2}
                        padding={1.5}>
                        <Box
                            display='flex'
                            justifyContent='space-between'
                            alignItems='center'
                            flexWrap='wrap'
                            gap={2}>
                            <Typography variant='h6'>Vendas por Mês</Typography>

                            <ToggleButtonGroup
                                size='small'
                                color='primary'
                                value={monthlyMetric}
                                exclusive
                                onChange={(_, value) => value && setMonthlyMetric(value)}>
                                <ToggleButton value='quantidade'>Quantidade</ToggleButton>
                                <ToggleButton value='valor'>Valor</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>

                        <Divider sx={{ marginY: 1.5 }} />

                        {monthlySales.length === 0 && (
                            <Typography variant='body2'>Nenhuma venda no periodo.</Typography>
                        )}

                        {monthlySalesSorted.length > 0 && monthlySeries.length > 0 && (
                            <Box sx={{ width: '100%', overflowX: 'auto' }}>
                                <Box sx={{ minWidth: 760 }}>
                                    <BarChart
                                        height={320}
                                        xAxis={[
                                            {
                                                id: 'meses',
                                                data: monthLabels,
                                                scaleType: 'band'
                                            }
                                        ]}
                                        yAxis={[
                                            {
                                                valueFormatter: (value) =>
                                                    monthlyMetric === 'valor' ? formatCurrency(value) : `${value || 0}`
                                            }
                                        ]}
                                        series={monthlySeries}
                                        tooltip={{ trigger: 'item' }}
                                    />
                                </Box>
                            </Box>
                        )}
                    </Box>
                )}

                {!loading && report && (
                    <Grid
                        container
                        spacing={2}
                        marginY={1}
                        marginRight={2}
                        columnSpacing={3}>
                        <Grid
                            item
                            xs={12}
                            lg={6}>
                            <Box
                                component={Paper}
                                elevation={0}
                                variant='outlined'
                                padding={1}>
                                <Typography variant='h6'>Vendas por Semana</Typography>
                                <Divider sx={{ marginY: 1 }} />
                                {salesWeeks.length === 0 && (
                                    <Typography variant='body2'>Nenhuma venda no periodo.</Typography>
                                )}
                                {currentWeek && (
                                    <Box marginBottom={0.5}>
                                        <Box
                                            display='flex'
                                            alignItems='center'
                                            justifyContent='space-between'>
                                            <IconButton
                                                onClick={() => setWeekIndex((prev) => prev + 1)}
                                                disabled={!canGoPrev}
                                                size='small'>
                                                <ChevronLeft />
                                            </IconButton>
                                            <Typography variant='subtitle2'>
                                                Semana {currentWeek.semana} / {currentWeek.ano} -{' '}
                                                {new Date(currentWeek.semana_inicio).toLocaleDateString('pt-BR', {
                                                    timeZone: 'UTC'
                                                })}{' '}
                                                ate{' '}
                                                {new Date(currentWeek.semana_fim).toLocaleDateString('pt-BR', {
                                                    timeZone: 'UTC'
                                                })}
                                            </Typography>
                                            <IconButton
                                                onClick={() => setWeekIndex((prev) => prev - 1)}
                                                disabled={!canGoNext}
                                                size='small'>
                                                <ChevronRight />
                                            </IconButton>
                                        </Box>
                                        <Grid
                                            container
                                            spacing={1}
                                            marginTop={0.5}>
                                            <Grid
                                                item
                                                xs={12}
                                                md={6}>
                                                <Card
                                                    elevation={0}
                                                    variant='outlined'>
                                                    <CardContent
                                                        sx={{ padding: 1, '&:last-child': { paddingBottom: 1 } }}>
                                                        <Typography
                                                            variant='subtitle2'
                                                            sx={{ color: cardLabelColor }}>
                                                            Quantidade Vendida
                                                        </Typography>
                                                        <Typography variant='h6'>
                                                            {currentWeek.total_vendas_semana || 0}
                                                        </Typography>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                            <Grid
                                                item
                                                xs={12}
                                                md={6}>
                                                <Card
                                                    elevation={0}
                                                    variant='outlined'>
                                                    <CardContent
                                                        sx={{ padding: 1, '&:last-child': { paddingBottom: 1 } }}>
                                                        <Typography
                                                            variant='subtitle2'
                                                            sx={{ color: cardLabelColor }}>
                                                            Receita
                                                        </Typography>
                                                        <Typography variant='h6'>
                                                            {formatCurrency(currentWeek.total_receita_semana)}
                                                        </Typography>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        </Grid>
                                        <Box marginTop={1}>
                                            <Table size='small'>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Pacote</TableCell>
                                                        <TableCell>Qtd.</TableCell>
                                                        <TableCell>Valor</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {currentWeek.pacotes.map((pacote) => (
                                                        <TableRow
                                                            key={`${currentWeek.ano}-${currentWeek.semana}-${pacote.produto_id}-${pacote.sku}`}>
                                                            <TableCell>{pacote.nome}</TableCell>
                                                            <TableCell>{pacote.quantidade_vendida}</TableCell>
                                                            <TableCell>
                                                                {formatCurrency(pacote.valor_recebido)}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            lg={6}>
                            <Box
                                component={Paper}
                                elevation={0}
                                variant='outlined'
                                padding={1}>
                                <Typography variant='h6'>Cupons Utilizados</Typography>
                                <Divider sx={{ marginY: 1 }} />
                                {coupons.length === 0 && (
                                    <Typography variant='body2'>Nenhum cupom encontrado.</Typography>
                                )}
                                {coupons.length > 0 && (
                                    <Table size='small'>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Cupom</TableCell>
                                                <TableCell>Qtd.</TableCell>
                                                <TableCell>Desconto</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {coupons.map((cupom) => (
                                                <TableRow key={cupom.cupom}>
                                                    <TableCell>{cupom.cupom}</TableCell>
                                                    <TableCell>{cupom.quantidade}</TableCell>
                                                    <TableCell>{formatCurrency(cupom.desconto_total)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                )}
            </Box>
        </Base>
    );
};
